import type { VoidFunctionComponent, ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './RangeStepper.module.less';

export type RangeStep = {
  label: string;
  value: string;
};

type Props = {
  id: string;
  steps: RangeStep[];
  onChange: (value: string) => void;
  defaultStepValue: string,
};

/**
 * A single-value range input component. Allows for labeled "steps" for every breakpoint.
 * Useful for when you need the user to select ONE specific value from a range of options.
 * The step options don't need to be numeric.
 *
 * @param {Props} props - The props for the component.
 * @param {string} props.id - A unique identifier for the input.
 * @param {RangeStep[]} props.steps - The "steps" for the slider (these are just SelectOption<string>s).
 * @param {(value: string) => void} props.onChange - The change handler for the slider.
 * @param {string} props.defaultStepValue - Which step is selected when the component is first rendered. Updating this will also trigger the onChange handler/will update the internal state here.
 */
const RangeStepper: VoidFunctionComponent<Props> = ({
  id,
  steps,
  onChange,
  defaultStepValue,
}) => {
  //  <input type="range" /> understands numbers, so we store the index of the selected step.
  const [currentStep, setCurrentStep] = useState<number>(0);

  //  Handler for the actual input slider (if they drag it around).
  const handleSliderChange = (event: ChangeEvent<HTMLInputElement>) => {
    const stepIndex = Number(event.target.value);
    updateSlider(stepIndex);
  };

  //  Update the slider based on the step index.
  const updateSlider = useCallback((stepIndex: number) => {
    setCurrentStep(stepIndex);

    // Find the selected step object
    const selectedStep = steps[stepIndex];
    if (selectedStep) {
      onChange(selectedStep.value);
    }
  }, [onChange, setCurrentStep, steps]);

  // Set the percentage for filling the progress bar
  const fillPercentage = (currentStep / (steps.length - 1)) * 100;

  //  This will update the internal state if the default step value changes.
  useEffect(() => {
    if (defaultStepValue) {
      const stepIndex = steps.findIndex((step) => step.value === defaultStepValue);
      updateSlider(stepIndex);
    }
  }, [steps, defaultStepValue, updateSlider]);

  return (
    <div className={styles.rangeStepper}>
      <div
        className={styles.rangeStepperTrack}
        style={useMemo(() => ({ backgroundSize: `${fillPercentage}% 100%` }), [fillPercentage])}
      />
      <input
        id={id}
        type="range"
        min="0"
        max={steps.length - 1}
        step="1"
        value={currentStep}
        onChange={handleSliderChange}
        className={styles.rangeStepperInput}
        list={`${id}-steps`}
      />
      <div className={styles.rangeStepperSteps}>
        {steps.map((step, index) => (
          <button
            type="button"
            key={step.value}
            onClick={() => updateSlider(index)}
            className={index === currentStep ? styles.active : ''}
          >
            {step.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RangeStepper;
